import React, { useEffect, useState } from "react";
import { View } from 'react-native'
import Input from "theme/Input";
import Button from "theme/Button";
import Text from "theme/Text";
import * as Animatable from 'react-native-animatable'
import _ from 'lodash'
import i18n from 'utils/i18n'
import { styles } from "../styles/styles";
import HelperPlatform from 'utils/helper-platform'
import HelperEncryption from 'utils/helper-encryption'
import EmployeeUtil from 'utils/employee-util'
import SecureStoreService from 'services/store/secure-store'
import { storeKeys } from 'services/store/keys'
import { constantsActive } from 'utils/constants/constants'
import { useInterval } from 'utils/hooks/use-interval'
import HelperSetting from 'utils/helper-setting'
import { settingsConstants } from 'utils/constants/settings-constants'
import AccountStorage from 'utils/accounts/AccountStorage'
import { configKeys } from 'utils/config/keys'
import HelperAlerts from 'utils/HelperAlerts'
import { TESTID } from '_shared/config/testID'
import HelperString from 'utils/helper-string'
import Icon from 'components/atoms/Icon'
import { IconSystemConstants } from 'utils/constants/IconNameConstants'
import { appRestart } from 'utils/helper-restart'
import { configClient } from 'components/provider/ConfigProvider'
import UserService from 'services/user'
import { resetData } from 'services/redux/data/actions'
import { resetManagement } from 'services/redux/management/actions'
import { resetAuthentication } from 'services/redux/authentication/actions'
import { resetCart } from 'services/redux/cart/actions'
import { useDispatch } from 'react-redux'

export const TokenStatus = {
	UNKOWN: 'unknown',
	VERIFIED: 'verified',
	ERROR: 'error'
}

const tokenLength = 6;

const Token = ( { tokenSentToMethod, mail, domain, emailStatus, onTokenStatusChange, onNameChange, reValidate = false } = {} ) => {
	
	const dispatch = useDispatch()
	
	const [ token, setToken ] = useState( '' )
	const [ tokenVerifying, setTokenVerifying ] = useState( false );
	const [ tokenResendCountdown, setTokenResetCountdown ] = useState( 30 );
	const [ tokenSentTo, setTokenSentTo ] = useState( tokenSentToMethod );
	
	const onChange = ( token = "" ) => {
		setToken( token );
	};
	
	const triggerAlert = ( title, message ) => {
		HelperAlerts.alert(
			title,
			message
		);
	}
	
	const getVariables = async () => {
		let variables = {};
		if( reValidate === true ) {
			variables = {
				verification: await SecureStoreService.get( storeKeys.EMPLOYEE_SESSION )
			}
		}
		
		return variables;
	}
	
	const verify = async () => {
		if( _.size( token ) < tokenLength ) {
			return;
		}
		
		EmployeeUtil.verifyToken( mail, token, {
			variables: await getVariables(),
			onStart: () => {
				setTokenVerifying( true )
			},
			onSuccess: ( result ) => {
				switch( result.status ) {
					case 'success':
						const authorisation = _.get( result, 'authorisation', null );
						const encryption_key = _.get( result, 'key', null );
						const guid = _.get( result, 'guid', null );
						
						if( _.isFunction( onNameChange ) ) {
							onNameChange( _.get( result, 'name', ''  ) );
						}
						
						setTokenVerifying( true )
						
						// create a token for all this
						AccountStorage.createNewIdentifier().then( ( account_identifier ) => {
							const accountData = {
								[ storeKeys.ACCOUNT_IDENTIFIER ]: account_identifier,
								[ storeKeys.EMPLOYEE_GUID ]: guid,
								[ storeKeys.ENCRYPTION_KEY ]: encryption_key,
								[ storeKeys.EMPLOYEE_SESSION ]: authorisation,
								[ storeKeys.DOMAIN ]: configClient.get( configKeys.CUSTOMER_ID ),
								[ storeKeys.DOMAIN_PREFIX ]: configClient.getApiCustomerIdPrefix(),
								[ storeKeys.EMPLOYEE_NAME ]: _.get( result, 'name', '' ),
								[ storeKeys.COMPANY_NAME ]: HelperSetting.get( settingsConstants.COMPANY_NAME )
							}
							
							return AccountStorage.storeAccount( account_identifier, accountData ).then( () => {
								HelperEncryption.setKey( encryption_key );
								
								return SecureStoreService.setMulti({
									[ storeKeys.EMPLOYEE_GUID ] : guid,
									[ storeKeys.ENCRYPTION_KEY ] : encryption_key,
									[ storeKeys.ACCOUNT_IDENTIFIER ]: account_identifier,
									[ storeKeys.EMPLOYEE_SESSION] : authorisation,
									[ storeKeys.DOMAIN ]: configClient.get( configKeys.CUSTOMER_ID ),
									[ storeKeys.DOMAIN_PREFIX ]: configClient.getApiCustomerIdPrefix()
								}).then( () => {
									if( _.isFunction( onTokenStatusChange ) ) {
										onTokenStatusChange( TokenStatus.VERIFIED );
									}
									
									setTimeout( appRestart, 1000 );
								});
							});
						}).catch( () => {
							setTokenVerifying( false );
							setToken( '' );
							HelperAlerts.alert(
								i18n.t( 'pages.more.accounts.save_error_title' ),
								i18n.t( 'pages.more.accounts.save_error' ),
							);
						});
						break;
					
					default:
						setTokenVerifying( false )
						setToken('')
						
						let error = _.isEmpty( result.error ) ? 'something_went_wrong' : result.error;
						
						triggerAlert(
							i18n.t(`page.authentication.domain.alerts.${error}.title`),
							i18n.t(`page.authentication.domain.alerts.${error}.message`)
						);
						break;
				}
			},
			onError: ( result ) => {
				setTokenVerifying( false )
				setToken('')
				
				triggerAlert(
					i18n.t( 'page.authentication.domain.alerts.something_went_wrong.title' ),
					i18n.t( 'page.authentication.domain.alerts.something_went_wrong.message' )
				);
			}
		});
	}
	
	const sendToken = async () => {
		EmployeeUtil.sendToken( mail, {
			variables: await getVariables(),
		} );
	}
	
	const resendToken = async () => {
		EmployeeUtil.sendToken( mail, {
			forceEmail: constantsActive.YES,
			variables: await getVariables(),
			onStart: () => {
				setToken('')
				resetTokenCountdown();
			},
			onSuccess: result => {
				setTokenSentTo( _.get( result, 'sent_to', { method: 'unknown', recipient: 'unknown@unknown' } ) )
				HelperAlerts.alert(
					i18n.t('page.authentication.domain.alerts.token_resend.title'),
					i18n.t('page.authentication.domain.alerts.token_resend.message')
				);
			}
		} );
	}
	
	const resetTokenCountdown = () => {
		setTokenResetCountdown( 30 );
	}
	
	const onTokenResendCountdown = () => {
		if( tokenResendCountdown > 0 ) {
			setTokenResetCountdown( tokenResendCountdown - 1 )
		}
	}
	
	const onLogout = () => {
		UserService.logout({
			resetData: () => {
				return dispatch(resetData());
			},
			resetManagement: () => {
				return dispatch(resetManagement());
			},
			resetAuthentication: () => {
				return dispatch(resetAuthentication());
			},
			resetCart: () => {
				return dispatch(resetCart());
			},
		});
	}
	
	useEffect( () => {
		setTokenSentTo( tokenSentToMethod );
	}, [ tokenSentToMethod ] );
	
	useEffect( () => {
		if( reValidate === true ) {
			sendToken();
		}
	}, [] )
	
	const getAccessoryLeft = () => {
		const items = [];
		
		switch( true ) {
			case tokenVerifying:
				items.push(
					<Animatable.Text
						key={ 'domain_icon_verifying' }
						animation={ 'rotate' }>
						<Icon icon={ IconSystemConstants.SPINNER} style={ [ styles.icon, styles.iconOrange ] } />
					</Animatable.Text>
				);
				break;
			default:
				items.push(
					<Icon icon={ IconSystemConstants.PLACEHOLDER } key={ 'domain_icon_success' } style={ [ styles.icon, styles.iconGrey ] } />
				);
				break;
		}
		
		return items;
	};
	
	const getAccessoryRight = () => {
		return (
			<Text
				style={ styles.textAccessoryRight }
				category={"s1"}>
				
				{ i18n.t( 'page.authentication.domain.token_hint' )}
			</Text>
		);
	}
	
	useEffect( () => {
		if( token.length === tokenLength ){
			verify();
		}
	}, [ token ]);
	
	useEffect( () => {
		setTokenResetCountdown( 30 );
	}, [] );
	
	useInterval( onTokenResendCountdown, 1000 );
	
	return (
		<Animatable.View
			animation={ "bounceIn"}>
			
			<View
				style={ styles.instructionsTextContainer }>
				
				{ reValidate === true ? (
					<Text style={ styles.instructionsText }>
						{ i18n.t( `page.authentication.domain.revalidate_token_sent` )}
					</Text>
				) : (
					<Text style={ styles.instructionsText }>
						{ i18n.t( `page.authentication.domain.token_sent_to.${tokenSentTo?.method}`, {
							recipient: tokenSentTo?.recipient
						} )}
					</Text>
				)}
			</View>
			
			<View
				style={ styles.itemContainer }>
				
				<Input
					autoFocus={ true }
					value={token}
					style={ styles.tokenInput }
					keyboardType={ HelperPlatform.isIOS() ? "phone-pad" : "number-pad"}
					textContentType="oneTimeCode"
					autoComplete="one-time-code"
					onChangeText={ onChange }
					maxLength={ tokenLength }
					accessoryLeft={ getAccessoryLeft }
					accessoryRight={ getAccessoryRight }
					accessoryRightWidth={ 'auto' }
					accessibilityLabel={ i18n.t('page.authentication.domain.token') }
					accessibilityHint={ i18n.t('page.authentication.domain.token_hint') }
					testID={ TESTID.AUTHENTICATION.TOKEN_INPUT }
				/>
			</View>
			
			<View
				style={styles.itemContainer}>
				
				<Button
					style={ styles.buttonResend }
					onPress={ resendToken }
					disabled={ tokenResendCountdown > 0 }
					key={ "token_resend_button"}>
					
					{ i18n.t('page.authentication.domain.token_button_resend') }
				</Button>
				
				{ tokenResendCountdown > 0 && (
					<View
						key={ HelperString.hash( "resend_countdown" ) }
						style={ styles.buttonResendCountdown }>
						
						<Text style={styles.buttonResendCountdownText}>
							( {tokenResendCountdown} )
						</Text>
					</View>
				)}
			</View>
			
			{ reValidate === true && (
				<View>
					<Button
						onPress={ onLogout }
						appearance="ghost">
						
						{ i18n.t( 'buttons.logout' ) }
					</Button>
				</View>
			)}
		</Animatable.View>
	);
}

export default Token;
